'use client';

import { Content } from '@prismicio/client';
import { PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';
import { RightArrow } from '@/assets/RightArrow';
import FloatingShapesBackground from '@/components/FloatingShapesBackground';
import '@/styles/FloatingShapes.css';
import Image from 'next/image';

export type FeatureHighlightSplitProps =
  SliceComponentProps<Content.FeatureHighlightSplitSlice>;

const FeatureHighlightSplit = ({
  slice,
}: FeatureHighlightSplitProps): JSX.Element => {
  const showFloatingShapes =
    slice.variation !== 'noCtaTwoCards' &&
    (slice.variation !== 'extended' ||
      slice.primary.floating_shapes_background !== false);
  const solidBg =
    slice.variation === 'extended' &&
    slice.primary.floating_shapes_background === false;

  const noCtaTwoCardsBg = slice.variation === 'noCtaTwoCards';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative text-white py-8 md:py-10 overflow-hidden${solidBg || noCtaTwoCardsBg ? ' bg-[#0F423A]' : ''}`}
    >
      {showFloatingShapes && <FloatingShapesBackground />}
      <div className="relative container mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-16 items-center">
        <div className="flex flex-col gap-6 text-center md:text-left">
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-4xl md:text-5xl font-bold">{children}</h2>
              ),
            }}
          />
          <PrismicRichText
            field={slice.primary.description}
            components={{
              paragraph: ({ children }) => (
                <p className="text-lg text-white/80">{children}</p>
              ),
            }}
          />
          {slice.variation !== 'noCtaTwoCards' && (
            <PrismicNextLink
              field={slice.primary.button_link}
              className="secondary-button self-center md:self-start"
            >
              <ArrowRightIcon className="arrow-icon" />
              {slice.variation === 'extended' && slice.primary.button_label
                ? slice.primary.button_label
                : 'Discover how VelX drives outcomes'}
            </PrismicNextLink>
          )}
        </div>

        <div className="flex flex-col gap-6">
          {slice.primary.features.map((item, index) => (
            <div
              key={index}
              className="rounded-2xl p-6"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(0, 0, 0, 0.1875), rgba(0, 0, 0, 0))',
                boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.08)',
              }}
            >
              <PrismicRichText
                field={item.feature_title}
                components={{
                  heading3: ({ children }) => (
                    <h3 className="flex items-center gap-2 text-[24px] font-semibold text-[#05F273]">
                      {slice.variation === 'extended' &&
                        (
                          item as Content.FeatureHighlightSplitSliceExtendedPrimaryFeaturesItem
                        ).feature_icon &&
                        (
                          item as Content.FeatureHighlightSplitSliceExtendedPrimaryFeaturesItem
                        ).feature_icon.url && (
                          <Image
                            src={
                              (
                                item as Content.FeatureHighlightSplitSliceExtendedPrimaryFeaturesItem
                              ).feature_icon.url || ''
                            }
                            alt={
                              (
                                item as Content.FeatureHighlightSplitSliceExtendedPrimaryFeaturesItem
                              ).feature_icon.alt || 'Feature Icon'
                            }
                            width={24}
                            height={24}
                            className="w-6 h-6"
                          />
                        )}
                      {slice.variation === 'default' && (
                        <RightArrow stroke="#34D399" />
                      )}
                      {/* No icons for noCtaTwoCards variation */}
                      {children}
                    </h3>
                  ),
                }}
              />
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {'feature_subtitle' in item && (item as any).feature_subtitle && (
                <p className="text-lg text-white/80 mt-2">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {(item as any).feature_subtitle}
                </p>
              )}
              {(slice.variation === 'default' ||
                slice.variation === 'noCtaTwoCards') &&
                (
                  item as Content.FeatureHighlightSplitSliceDefaultPrimaryFeaturesItem
                ).feature_description && (
                  <PrismicRichText
                    field={
                      (
                        item as Content.FeatureHighlightSplitSliceDefaultPrimaryFeaturesItem
                      ).feature_description
                    }
                    components={{
                      paragraph: ({ children }) => (
                        <p className="text-white mt-2">{children}</p>
                      ),
                    }}
                  />
                )}
            </div>
          ))}
        </div>
      </div>
      {slice.variation === 'default' ||
        (slice.variation === 'extended' && slice.primary.gradient_on_botton && (
          <div className="absolute bottom-0 left-0 w-full h-28 md:h-38 z-10 bg-gradient-to-t from-[#0E1010] to-transparent pointer-events-none" />
        ))}
    </section>
  );
};

export default FeatureHighlightSplit;
